/**
 * Pull the human-readable message out of an error response body, whatever shape
 * it arrived in. Takes the raw body (Angular's `HttpErrorResponse.error`, or any
 * parsed response) so it carries no framework dependency.
 *
 * Verified against the real `GlobalExceptionFilter` (2026-09-08): a thrown
 * `HttpException` puts its text at **top-level `message`** in every
 * environment, and repeats it under `details.message` outside prod. But not
 * every failure reaches us through that filter — a proxy, a gateway, or a
 * non-JSON response yields a bare string body or nothing at all, and those were
 * the cases that previously collapsed into an unhelpful generic.
 *
 * Order: `details.message` **when it's an array** → top-level `message` unless
 * it's one of Nest's derived placeholders → `details.message` as a string →
 * a plain-string body → nothing.
 *
 * The array goes first because of a specific Nest behaviour: `ValidationPipe`
 * throws with `message` as a `string[]`, and `HttpException` only adopts its
 * response's `message` as the top-level string when it IS a string. For an
 * array it falls back to a name derived from the class — literally
 * `"Bad Request Exception"` — so the useful text survives only inside
 * `details`. Reading top-level first would surface that placeholder and
 * discard the real reasons.
 */
export function extractServerMessage(body: unknown): string | null {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    // A proxy's HTML error page is not a message worth showing anyone.
    return trimmed && !trimmed.startsWith('<') ? trimmed : null;
  }
  if (!body || typeof body !== 'object') {
    return null;
  }

  const record = body as { message?: unknown; details?: { message?: unknown } };
  const nested = record.details?.message;
  if (Array.isArray(nested)) {
    const joined = nested
      .filter((m) => typeof m === 'string')
      .join(' ')
      .trim();
    if (joined) {
      return joined;
    }
  }

  for (const candidate of [record.message, nested]) {
    const text = Array.isArray(candidate)
      ? candidate.filter((m) => typeof m === 'string').join(' ')
      : candidate;
    if (typeof text === 'string' && text.trim() && !isDerivedName(text)) {
      return text.trim();
    }
  }
  return null;
}

/**
 * True for the placeholder Nest builds from an exception's class name when it
 * has no usable string message ("Bad Request Exception", "Conflict Exception").
 * Showing one of these is strictly worse than showing the HTTP status, which is
 * what callers fall back to.
 */
export function isDerivedName(text: string): boolean {
  return /^[A-Z][a-z]+( [A-Z][a-z]+)* Exception$/.test(text.trim());
}
